import PropTypes from 'prop-types';
import React, { useCallback } from 'react';

import Image from 'components/ImageV2';
import { IMAGE_RATIOS, IMAGE_WIDTHS } from 'constants/images';
import I18n from 'utils/I18n';
import Rudderstack from 'utils/Rudderstack';
import GENERIC_CTA_CLICKED_EVENT from 'utils/Rudderstack/constants';

import Pin from './Pin';

const CarouselSlide = ({
  handle,
  featuredImage,
  googleMapsUrl,
  phoneNumber,
  selectedStore,
  streetAddress,
  storeName,
}) => {
  const onCarouselSlideClick = useCallback(({ handle, storeName }) => {
    Rudderstack.callToActionClicked({
      location: GENERIC_CTA_CLICKED_EVENT.LOCATIONS.STORE_LOCATION_CARD,
      name: storeName,
      type: GENERIC_CTA_CLICKED_EVENT.TYPES.CARD,
      url: `pages/${handle}`,
    });
    // should allow the clicked anchor tag to open the url
    return true;
  }, []);

  return (
    <div className="bg-white">
      {featuredImage?.file?.url && (
        <a
          className="block aspect-video overflow-hidden"
          href={`/pages/${handle}`}
          onClick={() => onCarouselSlideClick({ handle, storeName })}
        >
          <Image
            className="block object-cover object-center"
            src={featuredImage.file.url}
            width={IMAGE_WIDTHS.W_1000}
            ratio={IMAGE_RATIOS.R_16x9}
          />
          {selectedStore?.handle === handle && (
            <div className="absolute right-6 -mt-6 hidden rounded-full bg-white p-2 md:block">
              <Pin isActive />
            </div>
          )}
        </a>
      )}

      <div className="grid grid-flow-row p-3 text-sm md:p-4 md:text-base">
        <div className="mb-1 flex items-center justify-between">
          <a href={`/pages/${handle}`}>
            <h3 className="truncate font-medium">
              {storeName.split(' - ').map((name, index) => {
                return index === 0 ? (
                  <span
                    key={name}
                    className="block text-base font-bold leading-none md:text-lg md:leading-tight"
                  >
                    {name}
                  </span>
                ) : (
                  <span key={name} className="">
                    {name}
                  </span>
                );
              })}
            </h3>
          </a>
        </div>
        <p>{streetAddress}</p>
        {phoneNumber && (
          <a className="hover:underline" href={`tel: ${phoneNumber}`}>
            {phoneNumber}
          </a>
        )}
        <div className="mt-2 grid grid-flow-col gap-2">
          <a
            className="flex w-full items-center justify-center rounded border-2 border-solid border-transparent bg-black p-2 text-center text-xs font-medium uppercase leading-none tracking-wide text-white transition ease-in-out hover:border-black hover:bg-white hover:text-black"
            href={`/pages/${handle}`}
          >
            {I18n.localize('storeLocator.storeInfo')}
          </a>
          <a
            className="ease-in-outhover:border-transparent flex w-full items-center justify-center rounded border-2 border-solid border-black bg-transparent p-2 text-center text-xs font-medium uppercase leading-none tracking-wide text-black transition hover:bg-black hover:text-white"
            href={googleMapsUrl}
          >
            {I18n.localize('storeLocator.directions')}
          </a>
        </div>
      </div>
    </div>
  );
};

CarouselSlide.propTypes = {
  featuredImage: PropTypes.shape({
    file: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
  googleMapsUrl: PropTypes.string,
  handle: PropTypes.string,
  phoneNumber: PropTypes.string,
  selectedStore: PropTypes.shape({
    handle: PropTypes.string,
  }),
  streetAddress: PropTypes.string,
  storeName: PropTypes.string,
};

export default CarouselSlide;
